/**
 * The phone and LinkedIn inputs, in one place.
 *
 * Both fields have a gap between what a person types, what the interface shows,
 * and what the database stores, and every form that re-implemented that gap
 * closed it slightly differently — one screen accepted a bare username, another
 * demanded a full URL; one reformatted digits as you typed, another stored
 * whatever was pasted. Same field, different rules, depending on where you
 * happened to be editing.
 *
 * The conversions themselves live in @shared/profileInputs and are shared with
 * the server. What these components add is the interface half: the country code
 * beside the number, the fixed linkedin.com/in/ prefix, and the reformatting on
 * every keystroke.
 *
 * Storage forms, which are what the API receives:
 *   phone     +14699550269   shown as (469) 955-0269
 *   linkedin  https://linkedin.com/in/jtebeau   shown as jtebeau after the prefix
 */
import { useEffect, useState, type CSSProperties } from "react";
import { Phone } from "lucide-react";
import {
  formatNationalPhone,
  formatPhoneForDisplay,
  LINKEDIN_PROFILE_PREFIX,
  normalizeInternationalPhone,
  normalizeLinkedInProfile,
} from "@shared/profileInputs";

/**
 * The countries offered, matching the create-account flow.
 *
 * The United States and Canada are separate entries even though both dial +1.
 * "US / CA" made a Canadian member pick a control that named someone else's
 * country, and the flag is what people actually scan for in a list like this —
 * a two-letter code has to be read, a flag does not.
 *
 * `region` is the identity here, not `code`: two entries share +1, so the dial
 * code alone cannot say which one is selected.
 */
export const COUNTRY_CODES = [
  { region: "US", code: "+1", flag: "🇺🇸", label: "United States" },
  { region: "CA", code: "+1", flag: "🇨🇦", label: "Canada" },
  { region: "GB", code: "+44", flag: "🇬🇧", label: "United Kingdom" },
  { region: "MX", code: "+52", flag: "🇲🇽", label: "Mexico" },
  { region: "AU", code: "+61", flag: "🇦🇺", label: "Australia" },
  { region: "NZ", code: "+64", flag: "🇳🇿", label: "New Zealand" },
  { region: "JP", code: "+81", flag: "🇯🇵", label: "Japan" },
  { region: "IN", code: "+91", flag: "🇮🇳", label: "India" },
  { region: "IE", code: "+353", flag: "🇮🇪", label: "Ireland" },
];

const baseInputStyle: CSSProperties = {
  width: "100%",
  minHeight: 42,
  padding: ".6rem .75rem",
  borderRadius: ".625rem",
  border: "1px solid rgba(255,255,255,.1)",
  background: "rgba(255,255,255,.04)",
  color: "#F8FAFC",
  fontFamily: "'Inter', sans-serif",
  fontSize: ".85rem",
  outline: "none",
  boxSizing: "border-box",
};

/**
 * Splits a stored E.164 number into the country code and the national part.
 *
 * Editing an existing record starts from `+14699550269`, not from an empty
 * field, so the form has to be able to run the display conversion backwards or
 * the first render would show the raw stored string.
 */
export function splitStoredPhone(stored: string | null | undefined): { countryCode: string; national: string } {
  const trimmed = stored?.trim();
  if (!trimmed) return { countryCode: "+1", national: "" };

  // Longest code first, so +1 does not swallow a number that begins +13 for a
  // country whose code happens to start with the same digit.
  const match = [...COUNTRY_CODES]
    .sort((a, b) => b.code.length - a.code.length)
    .find(country => trimmed.startsWith(country.code));

  if (!match) {
    // Unknown or already-national. Shown as-is rather than mangled into a
    // country it may not belong to.
    return { countryCode: "+1", national: formatPhoneForDisplay(trimmed) ?? trimmed };
  }

  const national = trimmed.slice(match.code.length);
  return { countryCode: match.code, national: formatNationalPhone(match.code, national) };
}

/**
 * The value to send to the API, or undefined when the field is empty.
 *
 * Throws on input that is present but unusable — a half-typed number — because
 * a submit handler needs to be told rather than silently sending nothing. Use
 * `draftPhoneValue` anywhere this runs before the person has finished typing.
 */
export function storedPhoneValue(countryCode: string, displayed: string): string | undefined {
  return normalizeInternationalPhone(countryCode, displayed);
}

/**
 * The same conversion, for code that runs on every keystroke.
 *
 * A partially typed number is not an error; it is a number being typed. Calling
 * the throwing version during render or in an onChange handler meant "469"
 * raised "Enter a complete phone number" — which, from a render, took the whole
 * drawer down with it rather than showing a message.
 *
 * Returns undefined until the input is complete enough to convert.
 */
export function draftPhoneValue(countryCode: string, displayed: string): string | undefined {
  try {
    return normalizeInternationalPhone(countryCode, displayed);
  } catch {
    return undefined;
  }
}

/**
 * A country code beside a national number that reformats as it is typed.
 *
 * `value` is the displayed national number — "(469) 955-0269" — not the stored
 * one. Call storedPhoneValue when submitting.
 */
export function PhoneInput({
  countryCode,
  value,
  onChange,
  label = "Phone number",
  id,
  disabled,
}: {
  countryCode: string;
  value: string;
  onChange: (next: { countryCode: string; value: string }) => void;
  label?: string;
  id?: string;
  disabled?: boolean;
}) {
  /*
   * Which country is selected, held here rather than derived from the dial code.
   *
   * The United States and Canada both dial +1, so the stored value cannot say
   * which of the two a member picked — the selection is a fact about the form,
   * not about the number. It follows the dial code when that changes from
   * outside (loading a record for editing), and otherwise stays where the member
   * put it.
   *
   * The consequence, stated plainly: a Canadian number reloaded from storage
   * shows the United States entry, because +1 is all that was saved. Recording
   * the region would need a column of its own.
   */
  const [region, setRegion] = useState(() => COUNTRY_CODES.find(country => country.code === countryCode)?.region ?? "US");

  useEffect(() => {
    setRegion(current => {
      const stillMatches = COUNTRY_CODES.find(country => country.region === current)?.code === countryCode;
      if (stillMatches) return current;
      return COUNTRY_CODES.find(country => country.code === countryCode)?.region ?? "US";
    });
  }, [countryCode]);

  const selectedCountry = COUNTRY_CODES.find(country => country.region === region) ?? COUNTRY_CODES[0];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(92px, 104px) minmax(0, 1fr)", gap: ".55rem" }}>
      <div style={{ position: "relative" }}>
        {/*
          Flag and dial code, and nothing else.
          
          There were briefly two flags — one painted over the control and another
          inside the option text — beside a country name the field was too narrow
          to finish, so it read "🇺🇸 🇺🇸 +1 · Unite". The flag already identifies
          the country, including between the two that share +1, so the name is
          redundant in a field this size.
          
          The full name is not lost: it is the select's accessible name, so a
          screen reader still announces "Phone country, United States" rather
          than a flag it cannot describe.
        */}
        <select
          aria-label={`Phone country: ${selectedCountry.label}`}
          value={region}
          disabled={disabled}
          /* The national number is cleared with the country: its formatting and
             its length limit are both country-specific, so keeping the old
             digits would leave a number formatted for the wrong one. */
          onChange={event => {
            const next = COUNTRY_CODES.find(country => country.region === event.target.value) ?? COUNTRY_CODES[0];
            setRegion(next.region);
            onChange({ countryCode: next.code, value: "" });
          }}
          style={{ ...baseInputStyle, paddingLeft: ".7rem", paddingRight: ".35rem", cursor: "pointer" }}
        >
          {COUNTRY_CODES.map(country => (
            <option key={country.region} value={country.region} title={country.label} style={{ background: "#0D1220" }}>
              {country.flag} {country.code}
            </option>
          ))}
        </select>
      </div>
      <div style={{ position: "relative" }}>
        <Phone size={14} aria-hidden="true" style={{ position: "absolute", left: ".7rem", top: "50%", transform: "translateY(-50%)", color: "#64748B", pointerEvents: "none" }} />
        <input
          id={id}
          aria-label={label}
          type="tel"
          autoComplete="tel-national"
          disabled={disabled}
          value={value}
          placeholder="Enter your phone number"
          onChange={event => onChange({ countryCode, value: formatNationalPhone(countryCode, event.target.value) })}
          style={{ ...baseInputStyle, paddingLeft: "2rem" }}
        />
      </div>
    </div>
  );
}

/** The username to show in the field for a stored profile URL. */
export function linkedinUsernameFromStored(stored: string | null | undefined): string {
  const trimmed = stored?.trim();
  if (!trimmed) return "";
  try {
    const normalized = normalizeLinkedInProfile(trimmed);
    return normalized?.slice(LINKEDIN_PROFILE_PREFIX.length) ?? "";
  } catch {
    // Not a shape the normalizer recognises. Shown as typed so an administrator
    // can see and correct it, rather than silently blanked.
    return trimmed;
  }
}

/** The value to send to the API, or undefined when empty. Throws if malformed. */
export function storedLinkedInValue(username: string): string | undefined {
  return normalizeLinkedInProfile(username);
}

/**
 * The same conversion for a LinkedIn username, for code that runs while typing.
 *
 * `storedLinkedInValue` throws on a username it cannot turn into a profile URL,
 * which is right at submit and wrong on a keystroke — half of a valid username
 * is usually not yet a valid username.
 */
export function draftLinkedInValue(username: string): string | undefined {
  try {
    return storedLinkedInValue(username);
  } catch {
    return undefined;
  }
}


/**
 * A fixed linkedin.com/in/ prefix with the username after it.
 *
 * `value` is the username alone. Pasting a full profile URL is accepted and
 * reduced to the username, because people paste what is on their clipboard.
 */
export function LinkedInInput({
  value,
  onChange,
  id,
  required,
  disabled,
}: {
  value: string;
  onChange: (username: string) => void;
  id?: string;
  required?: boolean;
  disabled?: boolean;
}) {
  const handleChange = (next: string) => {
    // A pasted URL is reduced to its username; anything else is left as typed so
    // the field does not fight someone mid-edit.
    if (next.includes("/") || next.includes("://")) {
      try {
        const normalized = normalizeLinkedInProfile(next);
        onChange(normalized?.slice(LINKEDIN_PROFILE_PREFIX.length) ?? "");
        return;
      } catch {
        /* fall through */
      }
    }
    onChange(next);
  };

  return (
    <div style={{ display: "flex", alignItems: "stretch", border: "1px solid rgba(255,255,255,.1)", borderRadius: ".625rem", overflow: "hidden", background: "rgba(255,255,255,.04)" }}>
      <span
        aria-hidden="true"
        style={{ display: "inline-flex", alignItems: "center", padding: "0 .72rem", borderRight: "1px solid rgba(255,255,255,.09)", color: "#7E8CA0", background: "rgba(255,255,255,.025)", fontFamily: "'Inter', sans-serif", fontSize: ".76rem", whiteSpace: "nowrap" }}
      >
        {/* The stored constant rather than a literal, so the prefix shown is exactly
            the prefix saved — scheme included — and the two cannot drift apart. */}
        {LINKEDIN_PROFILE_PREFIX}
      </span>
      {/* No placeholder: the prefix already shows the expected shape, and a
          sample username reads as a prefilled value. */}
      <input
        id={id}
        aria-label="LinkedIn username"
        autoComplete="url"
        required={required}
        disabled={disabled}
        value={value}
        onChange={event => handleChange(event.target.value)}
        style={{ ...baseInputStyle, border: 0, borderRadius: 0, background: "transparent", minWidth: 0 }}
      />
    </div>
  );
}

/** The hint that belongs under a LinkedIn field, so the wording matches too. */
export const LINKEDIN_INPUT_HINT = "Type only your username, or paste your full LinkedIn profile URL.";
